import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import type { ClientesPagina, CompletarPelaRfbResultado, RegimeTributario, SituacaoCliente } from '@sgc/contracts';
import { UFS, rotuloAnexos } from '@sgc/contracts';
import { get, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { OPCOES_REGIME, OPCOES_SITUACAO, ROTULO_REGIME, ROTULO_SITUACAO, formatarDocumento } from '../lib/formato';
import { Campo, Sel } from '../ui/campos';
import { Pill, Sec } from '../ui/layout';
import { ImportarClientes } from '../telas/clientes/importar';
import { useToast } from '../ui/toast';

const Busca = z.object({
  busca: z.string().optional(),
  situacao: z.enum(['ATIVA', 'SUSPENSA', 'BAIXADA']).optional(),
  regime: z.enum(['SIMPLES', 'MEI', 'PRESUMIDO', 'REAL', 'IMUNE_ISENTA', 'PF']).optional(),
  uf: z.string().optional(),
  page: z.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_app/clientes/')({
  validateSearch: Busca,
  component: Clientes,
});

const corSituacao = (s: SituacaoCliente) => (s === 'ATIVA' ? 'verde' : s === 'SUSPENSA' ? 'ambar' : 'verm');

function Clientes() {
  const s = Route.useSearch();
  const nav = useNavigate({ from: Route.fullPath });
  const { eu } = useAuth();
  const carteira = !!eu?.escritorio?.carteira;
  const papel = eu?.escritorio?.papel;
  const podeEditar = papel === 'ADMIN' || papel === 'SUPERVISOR' || papel === 'OPERADOR';
  const podeImportar = papel === 'ADMIN' || papel === 'SUPERVISOR';
  const [importando, setImportando] = useState(false);
  const toast = useToast();
  const contatos = useMutation({
    mutationFn: () => post<{ clientes: number; criados: number; semEmail: number }>('/v1/clientes/contatos/do-cadastro', {}),
    onSuccess: (r) => toast.ok(r.criados ? `${r.criados} contato(s) criado(s) para ${r.clientes} cliente(s)${r.semEmail ? ` · ${r.semEmail} cliente(s) sem e-mail no cadastro` : ''}` : r.semEmail ? `nada a criar: ${r.semEmail} cliente(s) sem contato também não têm e-mail no cadastro` : 'todos os clientes já têm contato'),
    onError: (e) => toast.erro(e),
  });
  // completa os campos em branco de cada cliente com a última consulta CNPJ já feita (sem consulta nova, sem custo)
  const completar = useMutation({
    mutationFn: () => post<CompletarPelaRfbResultado>('/v1/clientes/completar-pela-rfb', {}),
    onSuccess: (r) => { void q.refetch(); toast.ok(r.completados ? `${r.completados} cadastro(s) completado(s) com dados da Receita${r.semConsulta ? ` · ${r.semConsulta} cliente(s) ainda sem consulta CNPJ` : ''}` : r.semConsulta ? `nada a completar agora: ${r.semConsulta} cliente(s) ainda não têm consulta CNPJ — abra a ficha e use "Buscar na Receita"` : 'os cadastros já estavam completos'); },
    onError: (e) => toast.erro(e),
  });
  const [buscaTxt, setBuscaTxt] = useState(s.busca ?? '');
  const perPage = 50;

  const q = useQuery({
    queryKey: ['clientes', s],
    queryFn: () => get<ClientesPagina>('/v1/clientes', { busca: s.busca, situacao: s.situacao, regime: s.regime, uf: s.uf, page: s.page ?? 1, perPage }),
    placeholderData: (prev) => prev,
  });
  const setFiltro = (patch: Partial<typeof s>) => void nav({ search: (old) => ({ ...old, ...patch, page: 1 }) });
  const total = q.data?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / perPage));
  const page = s.page ?? 1;

  return (
    <>
      <div className="topbar">
        <div><h1>Clientes</h1><div className="sub">{total} cliente(s){carteira ? ' — você vê só a sua carteira (os clientes liberados para você e os de que é responsável)' : ''}</div></div>
        <div className="acoes">
          {podeImportar && <button className="btn" type="button" disabled={contatos.isPending} title="cliente com e-mail no cadastro e nenhum contato: o e-mail vira contato (é para os contatos que vão guias, lembretes e mensagens)" onClick={() => { if (confirm('Criar contatos a partir do e-mail do cadastro para os clientes que ainda não têm nenhum contato?')) contatos.mutate(); }}>{contatos.isPending ? 'criando…' : 'Criar contatos pelos e-mails'}</button>}
          {podeImportar && <button className="btn" type="button" disabled={completar.isPending} title="preenche os campos EM BRANCO de cada cliente (endereço, natureza jurídica, CNAEs, sócios…) com a última consulta CNPJ já feita — sem consultar de novo, sem custo" onClick={() => { if (confirm('Completar os cadastros com as consultas CNPJ que já foram feitas na Receita?\n\nSó entram campos que estão em branco — nada do que você digitou é trocado. Não faz consulta nova nem gera custo.')) completar.mutate(); }}>{completar.isPending ? 'completando…' : 'Completar pela Receita'}</button>}
          {podeImportar && <button className="btn" type="button" onClick={() => setImportando(true)}>Importar CSV</button>}
          {podeEditar && <Link className="btn primario" to="/clientes/novo">+ Novo cliente</Link>}
        </div>
      </div>
      <div className="conteudo">
        <Sec titulo="Filtros">
          <form className="fgrid" onSubmit={(e) => { e.preventDefault(); setFiltro({ busca: buscaTxt || undefined }); }}>
            <Campo className="c4" rotulo="Buscar" valor={buscaTxt} onChange={setBuscaTxt} placeholder="razão social, fantasia, código ou CNPJ" />
            <Sel className="c2" rotulo="Situação" valor={s.situacao ?? ''} vazio="todas" opcoes={OPCOES_SITUACAO} onChange={(v) => setFiltro({ situacao: (v || undefined) as SituacaoCliente | undefined })} />
            <Sel className="c3" rotulo="Regime" valor={s.regime ?? ''} vazio="todos" opcoes={OPCOES_REGIME} onChange={(v) => setFiltro({ regime: (v || undefined) as RegimeTributario | undefined })} />
            <Sel className="c1" rotulo="UF" valor={s.uf ?? ''} vazio="—" opcoes={UFS.map((u) => [u, u] as const)} onChange={(v) => setFiltro({ uf: v || undefined })} />
            <div className="c2"><button className="btn" type="submit" style={{ width: '100%' }}>Buscar</button></div>
          </form>
        </Sec>

        <div className="tbwrap">
          <table className="tb">
            <thead><tr><th>Código</th><th>Razão social</th><th>CNPJ/CPF</th><th>UF</th><th>Regime</th><th>Situação</th></tr></thead>
            <tbody>
              {q.isLoading && <tr><td className="vazio" colSpan={6}>carregando…</td></tr>}
              {q.data?.itens.length === 0 && <tr><td className="vazio" colSpan={6}>nenhum cliente encontrado — cadastre um ou importe a base em CSV</td></tr>}
              {q.data?.itens.map((c) => (
                <tr key={c.id}>
                  <td className="mono dim">{c.codigo ?? ''}</td>
                  <td><Link to="/clientes/$id" params={{ id: c.id }}><b>{c.razaoSocial}</b></Link>{c.nomeFantasia && <div className="dim">{c.nomeFantasia}</div>}
                    {c.situacao === 'ATIVA' && (c.obrigacoesMarcadas === 0 || c.contatosAtivos === 0 || !c.regimeAtual || !c.cnaePrincipal || !c.endereco) && <div className="pendencias">
                      {c.obrigacoesMarcadas === 0 && <Link to="/clientes/$id" params={{ id: c.id }} search={{ aba: 'obrigacoes', enquete: true }} title="nenhuma obrigação marcada: responda a Enquete"><Pill cor="ambar">sem obrigação marcada</Pill></Link>}
                      {c.contatosAtivos === 0 && <Link to="/clientes/$id" params={{ id: c.id }} search={{ aba: 'portal' }} title="sem contato: ninguém recebe e-mail nem entra no portal"><Pill cor="ambar">sem contato</Pill></Link>}
                      {!c.regimeAtual && <Link to="/clientes/$id" params={{ id: c.id }} search={{ aba: 'regime' }} title="sem regime tributário: as obrigações que dependem de regime não geram tarefa"><Pill cor="ambar">sem regime</Pill></Link>}
                      {!c.cnaePrincipal && <Link to="/clientes/$id" params={{ id: c.id }} search={{ aba: 'dados' }} title="sem CNAE: a Enquete não tem palpite e a ficha sai incompleta"><Pill>sem CNAE</Pill></Link>}
                      {!c.endereco && <Link to="/clientes/$id" params={{ id: c.id }} search={{ aba: 'dados' }} title="sem endereço: contratos e ficha cadastral saem sem ele"><Pill>sem endereço</Pill></Link>}
                    </div>}</td>
                  <td className="mono">{formatarDocumento(c.documento)}</td>
                  <td>{c.uf}</td>
                  <td>{c.regimeAtual ? <>{ROTULO_REGIME[c.regimeAtual]}{c.anexosAtuais?.length ? <span className="dim"> · {c.anexosAtuais.length > 1 ? 'anexos' : 'anexo'} {rotuloAnexos(c.anexosAtuais)}</span> : null}</> : <span className="dim">—</span>}</td>
                  <td><Pill cor={corSituacao(c.situacao)}>{ROTULO_SITUACAO[c.situacao]}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {paginas > 1 && (
          <div className="linha-acoes">
            <button className="btn mini" type="button" disabled={page <= 1} onClick={() => void nav({ search: (o) => ({ ...o, page: page - 1 }) })}>‹ anterior</button>
            <span className="mini">página {page} de {paginas}</span>
            <button className="btn mini" type="button" disabled={page >= paginas} onClick={() => void nav({ search: (o) => ({ ...o, page: page + 1 }) })}>próxima ›</button>
          </div>
        )}
      </div>
      {importando && <ImportarClientes onFechar={() => { setImportando(false); void q.refetch(); }} />}
    </>
  );
}
